import React, {useEffect, useState} from 'react';
import {
  fetchRequest,
  securedFetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import {API_URL} from '../../constants';
import {LazyStripePaymentFlow} from '../../common/LazyLoader';
import * as Sentry from '@sentry/react';
import {useRecoilValue} from 'recoil';
import {userState} from '../../recoil/user/userAtom';
import {Dialog} from '@mui/material';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';
import {ErrorCard, SkeletonBlock} from '../../common/design';

let inIframe = false;
try {
  inIframe = window.self !== window.top;
} catch (e) {
  inIframe = true;
}
console.log(`inIframe: ${inIframe}`);

export const ServiceOrderCheckout = (props: $TSFixMe) => {
  const search = new URLSearchParams(window.location.search);
  const [serviceOrder, setServiceOrder] = React.useState<$TSFixMe>(null);
  const [clientSecret, setClientSecret] = useState<string>();
  const [loadError, setLoadError] = useState(false);
  const user = useRecoilValue(userState);
  const muiTheme = useTheme();
  const isCompact = useMediaQuery(muiTheme.breakpoints.down(601));

  const [redirectUrl, setRedirectUrl] = React.useState<string | null>(
    inIframe && search.has('redirectUrl') ? search.get('redirectUrl') : null,
  );

  const getServiceOrderData = () => {
    setLoadError(false);
    return fetchRequest(`${API_URL}/service-orders/${props.match.params.uid}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((res) => {
        setServiceOrder(res);
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        setLoadError(true);
        Sentry.withScope(function (scope) {
          scope.setTag(
            'section',
            'ServiceOrderCheckout.tsx getServiceOrderData()',
          );
          Sentry.captureException(err);
        });
      });
  };

  useEffect(() => {
    getServiceOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getNonceData = () => {
    const body = {
      productIds: 'SERVICE_ORDER',
      recId: serviceOrder.id,
      recModel: 'SERVICE_ORDER',
      redirectUrl: window.location.href,
      productType: 'SERVICE_ORDER',
    };

    const requestFunction = !user ? fetchRequest : securedFetchRequest;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };
    return requestFunction(`${API_URL}/create-payment-intent`, options)
      .then((response) => response.text())
      .then((CLIENT_TOKEN_FROM_SERVER) => {
        setClientSecret(CLIENT_TOKEN_FROM_SERVER);
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        setLoadError(true);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'StripePaymentFlow.tsx getNonceData()');
          Sentry.captureException(err);
        });
      });
  };

  useEffect(() => {
    if (inIframe && search.has('redirect_status')) {
      const redirectStatus = search.get('redirect_status');
      const payload = {redirectStatus: redirectStatus};
      window.parent.postMessage(JSON.stringify(payload), '*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (serviceOrder) {
      getNonceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrder]);

  /** Note: if a redirectUrl has not been specified and extracted from the window.location.href query params then we will
   * leverage the serviceOrder._links.service_order_redirect_url to set the redirectUrl state. *
   */
  useEffect(() => {
    if (serviceOrder && !redirectUrl) {
      setRedirectUrl(serviceOrder._links.service_order_redirect_url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrder]);

  const handleRetry = () => {
    if (!serviceOrder) {
      getServiceOrderData();
    } else {
      getNonceData();
    }
  };

  if (loadError) {
    return (
      <Dialog
        open={true}
        fullWidth
        maxWidth="md"
        fullScreen={isCompact}
        PaperProps={{style: {borderRadius: isCompact ? 0 : 10}}}>
        <Box p={3}>
          <ErrorCard
            headline="We could not load your payment"
            message="Something went wrong on our end. Check your connection and try again."
            onRetry={handleRetry}
          />
        </Box>
      </Dialog>
    );
  }

  if (!serviceOrder || !clientSecret) {
    return (
      <Dialog
        open={true}
        fullWidth
        maxWidth="md"
        fullScreen={isCompact}
        PaperProps={{style: {borderRadius: isCompact ? 0 : 10}}}>
        <Box p={3} display="flex" flexDirection="column" gap="14px">
          <SkeletonBlock height={22} width="45%" />
          <SkeletonBlock height={44} radius="sm" />
          <SkeletonBlock height={44} radius="sm" />
          <SkeletonBlock height={44} radius="sm" width="70%" />
          <SkeletonBlock height={48} radius="md" sx={{marginTop: '10px'}} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="md"
      fullScreen={isCompact}
      PaperProps={{style: {borderRadius: isCompact ? 0 : 10}}}>
      <Box p={1}>
        <LazyStripePaymentFlow
          redirectUrl={redirectUrl}
          clientSecret={clientSecret}
          key={'payment_flow_service_order'}
          fixedFooter
        />
      </Box>
    </Dialog>
  );
};
